/**
 * @typedef {object} CardContainerUi
 * @property {HTMLElement} holder - The grid the cards render into.
 * @property {HTMLButtonElement} prevPage
 * @property {HTMLButtonElement} nextPage
 * @property {HTMLElement} pageNumber - Where the current page number is written.
 * @property {HTMLElement} pageControl - The paging bar, hidden while a page loads.
 * @property {HTMLElement} pageLoading - The loading notice shown while a page loads.
 * @property {HTMLElement} labelsNotFound - The "no matches" notice for a filtered query that found nothing.
 * @property {HTMLElement} expandedView - The `.gallery-expanded-view` element the expanded view is built in.
 */

/**
 * What the page was opened with, as gallery.scala.html wrote it into the page.
 * @typedef {object} PageParams
 * @property {Record<string, any>} initialFilters - The filters the server parsed out of the URL.
 * @property {Record<string, string>} regionNames - Region names for the cards' location line, keyed by region id.
 * @typedef {ViewerParams & PageParams} CardContainerParams
 */

import i18next from 'i18next';
import { util } from '../../common/utilities.js';
import { closeReasonPopover } from '../../common/ReasonPopover.js';
import { Card } from './Card.js';
import { CardBucket } from './CardBucket.js';
import { isSorted } from './cardOrder.js';
import { ExpandedView } from '../expandedview/ExpandedView.js';
import '../../common/utilitiesSidewalk.js';
/** @typedef {import('../data/Tracker.js').Tracker} Tracker */
/** @typedef {import('../filter/GalleryFilter.js').GalleryFilter} GalleryFilter */
/** @typedef {import('../expandedview/ExpandedView.js').ViewerParams} ViewerParams */

/**
 * Card Container module. This is responsible for managing the Card objects that are to be rendered.
 *
 * Construct instances via the `static async create()` factory, which fetches the first batch of labels and builds
 * the ExpandedView before resolving.
 */
export class CardContainer {
  // The number of labels to grab from database on initial page load.
  static #initialLoad = 30;

  // Cards per page, by mode. Review-list mode has no sidebar, so its grid runs full width and falls to four, three
  // and two columns as the window narrows; twelve fills every one of those evenly, where nine leaves a ragged row
  // on four. The filtered grid sits beside the 275px sidebar and never reaches four columns, so it keeps nine.
  static #listCardsPerPage = 12;

  static #filteredCardsPerPage = 9;

  #uiCardContainer;
  #params;
  #cardFilter;
  #tracker;

  #currentLabelTypes;
  #currentPage = 1;
  #lastPage = false;
  #pageNumberDisplay = null;
  #expandedView;

  // Map Cards to a CardBucket containing Cards of their label type.
  #cardsByType = {
    CurbRamp: new CardBucket(),
    NoCurbRamp: new CardBucket(),
    Obstacle: new CardBucket(),
    SurfaceProblem: new CardBucket(),
    Other: new CardBucket(),
    Occlusion: new CardBucket(),
    NoSidewalk: new CardBucket(),
    Crosswalk: new CardBucket(),
    Signal: new CardBucket(),
  };

  // Keep track of labels we have loaded already as to not grab the same label from the backend.
  #loadedLabelIds = new Set();

  // The position each loaded label arrived at, across fetches, for sorted mode (#2705). Fetch order is the server's
  // order (every fetch returns the next ranked labels, and the cache is emptied whenever that stops being true), and
  // it is what the cards are paged by rather than their own severity or votes: those change under the admin as they
  // vote or edit in the expanded view, and re-deriving the order from them moved cards between pages mid-review.
  /** @type {Map<string, number>} Keyed as Card.getLabelId() types its answer. */
  #fetchOrder = new Map();

  // Current labels being displayed of current type based off filters.
  #currentCards = new CardBucket();

  // Review-list mode (#5444): the page was opened with ?labelIds=, so these exact labels are shown in this order and
  // the filter sidebar isn't rendered at all. Nothing is fetched after the first query — the list is the whole set.
  /** @type {number[]} */
  #listLabelIds = [];
  #listMode = false;

  // Set when a page rendered before the expanded view existed, so #init can hand it that page once it does. The
  // opening query is fired before the view is built and can land first; see #notifyExpandedViewRendered().
  #expandedViewHooksDeferred = false;

  // Whether the page just rendered has any cards on it, so the deferred handover knows whether the paging control
  // belongs on screen.
  #pageHasCards = false;

  /**
   * @param {CardContainerUi} uiCardContainer - The card grid, its paging controls, and the notices around them.
   * @param {CardContainerParams} params - What the page was opened with.
   * @param {GalleryFilter} cardFilter - The sidebar, which owns the filter state the cards are fetched for.
   * @param {Tracker} tracker - Logs the paging.
   */
  constructor(uiCardContainer, params, cardFilter, tracker) {
    this.#uiCardContainer = uiCardContainer;
    this.#params = params;
    this.#cardFilter = cardFilter;
    this.#tracker = tracker;

    this.#listLabelIds = params.initialFilters.labelIds ?? [];
    this.#listMode = this.#listLabelIds.length > 0;

    // The sidebar is built first and owns the filter state, so the initial selection comes from it, not the page.
    this.#currentLabelTypes = cardFilter.getStatus().currentLabelTypes;
    cardFilter.onUpdate(() => this.updateCardsByFilter());
    cardFilter.onSortChange(() => this.updateCardsBySort());
  }

  /**
   * Creates a CardContainer, fetches the first batch of labels, and builds the ExpandedView.
   * @param {CardContainerUi} uiCardContainer - The card grid, its paging controls, and the notices around them.
   * @param {CardContainerParams} params - What the page was opened with.
   * @param {GalleryFilter} cardFilter - The sidebar, which owns the filter state the cards are fetched for.
   * @param {Tracker} tracker - Logs the paging.
   * @returns {Promise<CardContainer>}
   */
  static async create(uiCardContainer, params, cardFilter, tracker) {
    const cardContainer = new CardContainer(uiCardContainer, params, cardFilter, tracker);
    await cardContainer.#init();
    return cardContainer;
  }

  async #init() {
    const uiCardContainer = this.#uiCardContainer;
    const initialFilters = this.#params.initialFilters;

    // Bind click actions to the forward/backward paging buttons.
    // The buttons also log a *Click event; the expanded view turns pages without one.
    uiCardContainer.nextPage.addEventListener('click', () => {
      this.nextPage();
      this.#tracker.push('NextPageClick', null, null);
    });
    uiCardContainer.prevPage.addEventListener('click', () => {
      if (this.#currentPage <= 1) return;
      this.prevPage();
      this.#tracker.push('PrevPageClick', null, null);
    });

    this.#pageNumberDisplay = uiCardContainer.pageNumber;
    this.#pageNumberDisplay.innerText = '1';
    uiCardContainer.pageControl.style.display = 'none';
    this.#cardFilter.disable();
    uiCardContainer.prevPage.disabled = true;

    // Grab first batch of labels to show.
    if (this.#listMode) {
      // The server already applied the only selection there is (the id list) and returned it in order, so the cards
      // go straight into the bucket — #collectCurrentCards would re-apply the sidebar's filters and regroup by type,
      // which is exactly the ordering the list exists to avoid.
      this.fetchLabels(
        [], this.#listLabelIds.length, [], [], undefined, undefined, undefined, undefined, this.#listLabelIds,
        undefined,
      ).then((result) => {
        // A failed request is not an empty list: rendering zero cards would show the filtered gallery's "No
        // matches, start exploring" copy and a "Showing 0 labels" count, telling a reviewer their queue is empty
        // when the server never answered. Say so instead, and leave the server-rendered count standing.
        if (result === null) {
          CardContainer.#showListError();
          uiCardContainer.pageLoading.style.display = 'none';
          // render() is never reached on this path, so the handover happens here instead: it releases the
          // filters and hands the view an empty page, where a ?labelId= still opens the label by id.
          this.#pageHasCards = false;
          this.#notifyExpandedViewRendered();
          return;
        }
        this.#currentCards = new CardBucket(result.newCards);
        this.#lastPage = this.#currentCards.getSize() <= this.#currentPage * this.getCardsPerPage();
        CardContainer.#renderListCount(this.#currentCards.getSize());
        CardContainer.#renderListTruncated();
        CardContainer.#renderUnavailableIds(result.unavailableLabelIds);
        this.render();
      });
    } else {
      const filters = this.#currentFilters();
      this.fetchLabels(
        filters.types,
        CardContainer.#initialLoad,
        filters.valOptions,
        Array.from(this.#loadedLabelIds),
        initialFilters.regionIds,
        filters.severities,
        filters.tagsByType,
        initialFilters.aiValidationOptions,
        undefined,
        filters.sort,
      ).then(() => {
        this.#currentCards = this.#collectCurrentCards(filters);
        this.#lastPage = this.#currentCards.getCards().length <= this.#currentPage * this.getCardsPerPage();
        this.render();
      });
    }
    this.#expandedView = await ExpandedView.create(uiCardContainer.expandedView, this.#params, this, this.#tracker);
    // The opening query is fired above, before this await, so the request is in flight while the viewer builds —
    // and can finish first. A page rendered in that window has nobody to hand itself to, so this is where it goes.
    if (this.#expandedViewHooksDeferred) this.#notifyExpandedViewRendered();
    // Add the click event for opening the ExpandedView when a card is clicked.
    const cardClickSelector
      = '.static-gallery-image, .additional-count, .ai-icon-marker-card, .gallery-image-placeholder';
    uiCardContainer.holder.addEventListener('click', (event) => {
      const target = event.target.closest(cardClickSelector);
      if (!target) return;
      // If the user clicks on the image body in the card, just use the provided id.
      // If they click the AI icon or the no-image placeholder, use the image id from the same card.
      // Otherwise, the user will have clicked on an existing "+n" icon on the card, meaning we need to acquire
      // the cardId from the card-tags DOM element (as well as perform an additional prepend to put the ID in
      // the correct form).
      let cardId;
      if (target.classList.contains('ai-icon-marker-card') || target.classList.contains('gallery-image-placeholder')) {
        const imageHolder = target.closest('.image-holder');
        const parentImage = imageHolder ? imageHolder.querySelector('.static-gallery-image') : null;
        cardId = parentImage ? parentImage.id : null;
      } else if (target.classList.contains('static-gallery-image')) {
        cardId = target.id;
      } else {
        cardId = `label_id_${target.closest('.card-tags').id}`;
      }
      if (!cardId) return;
      this.#expandedView.updateCardIndex(this.#findCardIndex(cardId));
    });

    // Tag pills are fitted in measured pixels at render time (TagDisplay), so a card-column width change
    // (rotation, the narrow-layout re-stack, a desktop resize) leaves stale fits; re-fit the visible cards.
    // TagDisplay rebuilds from scratch, so re-running is idempotent; the width guard plus trailing debounce
    // keep it quiet during continuous resizes and height-only changes (which ResizeObserver also reports).
    let lastTagFitWidth = null;
    let tagRefitTimer = null;
    new ResizeObserver((entries) => {
      const width = entries[0].contentRect.width;
      if (width === lastTagFitWidth) return;
      lastTagFitWidth = width;
      clearTimeout(tagRefitTimer);
      tagRefitTimer = setTimeout(() => this.getCurrentPageCards().forEach((card) => card.refitTags()), 150);
    }).observe(uiCardContainer.holder);
  }

  /**
   * Returns the index of a card in the current CardBucket in use.
   *
   * @param {string} id - The id of the image Id to find.
   * @returns {number} The index of the matching card in the current CardBucket.
   */
  #findCardIndex(id) {
    return this.#currentCards.findCardIndexByImageId(id);
  }

  /**
   * Gets a card from the current CardBucket given an index.
   *
   * @param {number} index - The index of the card to find.
   * @returns {Card} The Card that has the matching index in the current CardBucket.
   */
  getCardByIndex(index) {
    return this.#currentCards.getCardByIndex(index);
  }

  /**
   * Turns to the next page. Also called by the expanded view when stepping past the page's last label.
   */
  nextPage() {
    this.#tracker.push('NextPage', null, {
      from: this.#currentPage,
      to: this.#currentPage + 1,
    });
    this.#setPage(this.#currentPage + 1);
    this.updateCardsNewPage();
  }

  /**
   * Turns back a page, if there is one. Also called by the expanded view when stepping back past the first label.
   */
  prevPage() {
    if (this.#currentPage <= 1) return;
    this.#tracker.push('PrevPage', null, {
      from: this.#currentPage,
      to: this.#currentPage - 1,
    });
    this.#uiCardContainer.nextPage.disabled = false;
    this.#setPage(this.#currentPage - 1);
    this.updateCardsNewPage();
  }

  /**
   * Sets the page number and the Prev button; Next is set in render(), once the last page is known.
   * @param {number} pageNumber
   */
  #setPage(pageNumber) {
    this.#uiCardContainer.prevPage.disabled = pageNumber <= 1;
    this.#currentPage = pageNumber;
    this.#pageNumberDisplay.innerText = pageNumber;
  }

  /**
   * Grab n labels of the specified label types, severities, and tags.
   *
   * @param {string[]} labelTypes - Label type names specifying which types of labels to grab.
   * @param {*} n - Number of labels to grab.
   * @param {string[]} validationOptions - Validation options for labels: correct, incorrect, and/or unvalidated.
   * @param {*} loadedLabels - Label Ids of labels already grabbed.
   * @param {*} regionIds - Region IDs the labels to be grabbed can be from (Set to undefined if N/A).
   * @param {*} severities - Severities the labels to be grabbed can have (Set to undefined if N/A).
   * @param {object} tagsByLabelType - Tags each label type is narrowed to, keyed by type name.
   * @param {string[]|undefined} aiValidationOptions - AI validation options: correct, incorrect, and/or unvalidated.
   * @param {number[]|undefined} labelIds - A review list (#5444). When non-empty the server ignores every filter
   *      above and returns exactly these labels in this order.
   * @param {string|undefined} sort - The admin's order (#2705), a `GallerySort` name. Random, or nothing, asks for
   *      the usual random sample; anything else asks for the next `n` labels in that order, the loaded ones excluded.
   * @returns {Promise<?{newCards: Card[], unavailableLabelIds: (number[]|undefined)}>} The new cards in server
   *     order and, for a review list, the ids it couldn't serve. Null (not an empty list) when the request failed.
   */
  async fetchLabels(
    labelTypes, n, validationOptions, loadedLabels, regionIds, severities, tagsByLabelType, aiValidationOptions,
    labelIds, sort,
  ) {
    const url = '/label/labels';
    const data = {
      label_types: labelTypes,
      n,
      validation_options: validationOptions,
      ...(regionIds !== undefined && { region_ids: regionIds }),
      ...(severities !== undefined && { severities }),
      ...(tagsByLabelType !== undefined && { tags_by_label_type: tagsByLabelType }),
      ...(aiValidationOptions !== undefined && { ai_validation_options: aiValidationOptions }),
      ...(labelIds !== undefined && labelIds.length > 0 && { label_ids: labelIds }),
      ...(isSorted(sort) && { sort }),
      loaded_labels: loadedLabels,
    };
    let response;
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json; charset=utf-8' },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error(`${url} responded ${res.status}`);
      response = await res.json();
      if (!Array.isArray(response?.labelsOfType)) throw new Error(`${url} returned no labels`);
    } catch (err) {
      console.error(err);
      return null;
    }
    const newCards = response.labelsOfType.map((labelProp) => {
      const card = new Card(labelProp, this.#params.regionNames, this.#tracker, this.#cardFilter);
      this.push(card);
      this.#loadedLabelIds.add(card.getLabelId());
      this.#fetchOrder.set(card.getLabelId(), this.#fetchOrder.size);
      return card;
    });
    return { newCards, unavailableLabelIds: response.unavailableLabelIds };
  }

  /**
   * Returns cards of current type.
   */
  getCards() {
    return this.#cardsByType;
  }

  /**
   * Returns cards of current type that are being rendered.
   */
  getCurrentCards() {
    return this.#currentCards;
  }

  /**
   * Finds a loaded card by its label id, whichever type bucket it sits in.
   * @param {number} labelId
   * @returns {?Card} Null when that label was never loaded.
   */
  findCardByLabelId(labelId) {
    for (const bucket of Object.values(this.#cardsByType)) {
      const card = bucket.getCards().find((c) => c.getLabelId() === labelId);
      if (card) return card;
    }
    return null;
  }

  /**
   * Push a card into the CardBucket of its label type.
   * @param {Card} card - Card to add.
   */
  push(card) {
    this.#cardsByType[card.getLabelType()].push(card);
  }

  /**
   * The filters the sidebar is currently reporting, in the shape the label query takes, plus the order.
   * @returns {{types: string[], valOptions: string[], severities: (string[]|undefined),
   *      tagsByType: Record<string, string[]>, sort: string}} The current filter state.
   */
  #currentFilters() {
    const cardFilter = this.#cardFilter;
    const types = cardFilter.getStatus().currentLabelTypes;
    // Severity is left out entirely when nothing selected can carry one — otherwise the "N/A" toggle, which those
    // labels all fall under, would silently decide whether they show at all.
    const anyHasSeverity = types.some((type) => util.misc.labelTypeHasSeverity(type));
    return {
      types,
      valOptions: cardFilter.getAppliedValidationOptions(),
      severities: anyHasSeverity ? cardFilter.getAppliedSeverities() : undefined,
      tagsByType: cardFilter.getAppliedTagsByType(),
      sort: cardFilter.getSort(),
    };
  }

  /**
   * Gathers the loaded cards that pass the current filters, from every selected label type.
   *
   * Cards accumulate across filter changes, so this re-applies the filters the server already applied when they were
   * fetched. Tags are per type — a curb ramp's "narrow" says nothing about an obstacle — so each type is filtered
   * against its own. In a sorted order (#2705) the gathered cards go back into the sequence they arrived in, which
   * is the server's order across the type buckets; that holds only because every card was fetched in this order
   * (see #resetLoadedCards).
   *
   * @param {{types: string[], valOptions: string[], severities: (string[]|undefined),
   *      tagsByType: Record<string, string[]>, sort: string}} filters - The filters from #currentFilters().
   * @returns {CardBucket} The cards to page through.
   */
  #collectCurrentCards({ types, valOptions, severities, tagsByType, sort }) {
    const bucket = new CardBucket();
    for (const type of types) {
      const cards = this.#cardsByType[type].copy();
      cards.filterOnTags(type === 'Occlusion' ? undefined : tagsByType[type]); // Occlusion labels carry no tags.
      cards.filterOnSeverities(severities);
      cards.filterOnValidationOptions(valOptions);
      cards.getCards().forEach((card) => bucket.push(card));
    }
    if (isSorted(sort)) {
      bucket.getCards().sort((a, b) => this.#fetchOrder.get(a.getLabelId()) - this.#fetchOrder.get(b.getLabelId()));
    }
    return bucket;
  }

  /**
   * Forgets every loaded card, so the next fetch starts over from the server.
   *
   * Sorted mode's cache rule (#2705): what the page holds has to be a prefix of the server's order under the
   * *current* filters, because a page is served from the cache until it runs short and only then fetches the next
   * ranked labels. Cards cached under a random order, or under a narrower filter, are not such a prefix — widening
   * the type filter, say, would show the cached lower-ranked cards of one type before the unloaded higher-ranked
   * cards of the other — so a change of order, or of a filter while ordered, starts the cache afresh. The random
   * Gallery keeps its cache across filter changes as it always has: no order, nothing to break.
   */
  #resetLoadedCards() {
    for (const type of Object.keys(this.#cardsByType)) this.#cardsByType[type] = new CardBucket();
    this.#loadedLabelIds = new Set();
    this.#fetchOrder = new Map();
  }

  /**
   * Updates Cards being shown when user moves to next/previous page.
   */
  updateCardsNewPage() {
    this.#refreshUI();

    // The list is fully loaded, so paging within it is pure arithmetic — never another query.
    if (this.#listMode) {
      this.#lastPage = this.#currentCards.getSize() <= this.#currentPage * this.getCardsPerPage();
      this.render();
      return;
    }

    const filters = this.#currentFilters();

    // With no label type selected there is nothing to ask the server for, and nothing to show.
    if (filters.types.length === 0) {
      this.#currentCards = new CardBucket();
      this.#lastPage = true;
      this.render();
      return;
    }

    this.#currentCards = this.#collectCurrentCards(filters);

    if (this.#currentCards.getSize() < this.getCardsPerPage() * this.#currentPage + 1) {
      // When we don't have enough cards of specific query to show on one page, see if more can be grabbed.
      this.fetchLabels(
        filters.types,
        this.getCardsPerPage() * 2,
        filters.valOptions,
        Array.from(this.#loadedLabelIds),
        this.#params.initialFilters.regionIds,
        filters.severities,
        filters.tagsByType,
        this.#params.initialFilters.aiValidationOptions,
        undefined,
        filters.sort,
      ).then(() => {
        this.#currentCards = this.#collectCurrentCards(filters);
        this.#lastPage = this.#currentCards.getCards().length <= this.#currentPage * this.getCardsPerPage();
        this.render();
      });
    } else {
      this.#lastPage = false;
      this.render();
    }
  }

  /**
   * When a filter is updated; update which Cards are shown.
   */
  updateCardsByFilter() {
    // List mode renders no filter controls, so nothing should reach this; guard anyway, since applying the sidebar's
    // empty state would silently throw the list away.
    if (this.#listMode) return;

    const newLabelTypes = this.#cardFilter.getStatus().currentLabelTypes;
    // Only need to refresh UI if the label types changed, since the tags are swapped out.
    if (newLabelTypes.join() !== this.#currentLabelTypes.join()) {
      this.#currentLabelTypes = newLabelTypes;
      this.#refreshUI();
    }

    // In an order, a filter change starts the cache over; see #resetLoadedCards for why.
    if (isSorted(this.#cardFilter.getSort())) this.#resetLoadedCards();
    this.#setPage(1);
    this.updateCardsNewPage();
  }

  /**
   * When the admin's "Sort by" changes (#2705): start over from page 1 in the new order.
   *
   * Always from an empty cache, even going back to Random: cards fetched in an order are the top of a ranking, not a
   * sample, and a random Gallery seeded with them would open on, say, the thirty most severe labels.
   */
  updateCardsBySort() {
    if (this.#listMode) return;
    this.#resetLoadedCards();
    this.#setPage(1);
    this.updateCardsNewPage();
  }

  /**
   * Renders current cards.
   */
  render() {
    const uiCardContainer = this.#uiCardContainer;
    // TODO: should we try to just empty in render method? Or assume it's was emptied in a method utilizing render?
    closeReasonPopover();
    uiCardContainer.holder.replaceChildren();

    const imagesToLoad = this.getCurrentPageCards();
    const imagePromises = imagesToLoad.map((img) => img.loadImage());
    this.#pageHasCards = imagesToLoad.length > 0;

    if (imagesToLoad.length > 0) {
      uiCardContainer.nextPage.disabled = this.#lastPage;

      // We wait for all the promises from grabbing pano images to resolve before showing cards.
      Promise.all(imagePromises).then(() => {
        imagesToLoad.forEach((card) => {
          card.render(uiCardContainer.holder);
        });
        uiCardContainer.pageLoading.style.display = 'none';
        this.#notifyExpandedViewRendered();
      });
    } else if (this.#listMode) {
      // "No matches. Start exploring to contribute more data!" answers a filtered search that found nothing; it
      // answers nothing about a list whose ids this city doesn't have, and with no sidebar to sit beside it, it is
      // absolutely positioned straight over the strip that does explain it.
      uiCardContainer.pageLoading.style.display = 'none';
      this.#notifyExpandedViewRendered();
    } else {
      // The stylesheet hides this notice, so it needs an explicit display value to show.
      uiCardContainer.labelsNotFound.style.display = 'block';
      uiCardContainer.pageLoading.style.display = 'none';
      this.#notifyExpandedViewRendered();
    }
  }

  /**
   * Hands a freshly rendered page over: the controls that act on it, then the expanded view's pending cross-page
   * navigation and any `?labelId=` deep link.
   *
   * This is the one place a page becomes live, and it runs exactly once per render, whichever of the opening query
   * and the viewer build finishes second. Both halves need the view to exist. The paging handlers close the
   * expanded view, and in the query-wins ordering the view isn't up yet, so the controls stay hidden and the
   * filters disabled until the handover can happen. And `restoreFromUrl()`
   * consumes the pending deep-link id on entry, so a render that skips it leaves the id set for a later render to
   * spring open — which is why every branch of `render()` calls this, empty pages included.
   */
  #notifyExpandedViewRendered() {
    if (!this.#expandedView) {
      this.#expandedViewHooksDeferred = true;
      return;
    }
    this.#expandedViewHooksDeferred = false;
    if (this.#pageHasCards) this.#uiCardContainer.pageControl.style.display = '';
    this.#cardFilter.enable();
    this.#expandedView.onPageCardsRendered();
    this.#expandedView.restoreFromUrl();
  }

  /**
   * Refreshes the UI after each query made by user.
   */
  #refreshUI() {
    // Close expanded views (if open) and empty cards from current page. The view is absent until the opening query
    // and the viewer build have both finished (see #notifyExpandedViewRendered).
    this.#expandedView?.closeExpandedView();
    closeReasonPopover();
    this.#uiCardContainer.holder.replaceChildren();

    // Place user back at top of page.
    window.scrollTo(0, 0);

    // Indicate query is sent, loading appropriate cards.
    const ui = this.#uiCardContainer;
    ui.pageLoading.style.display = '';

    // Disable interactable UI elements while query loads.
    this.#cardFilter.disable();
    ui.labelsNotFound.style.display = 'none';
    ui.pageControl.style.display = 'none';
  }

  /**
   * Flush all Cards currently being rendered.
   */
  clearCurrentCards() {
    this.#currentCards = new CardBucket();
  }

  getCurrentPage() {
    return this.#currentPage;
  }

  /**
   * Get the cards that form the current page.
   * @returns {Card[]} Array of cards from the current page.
   */
  getCurrentPageCards() {
    let idx = (this.#currentPage - 1) * this.getCardsPerPage();
    const cardBucket = this.#currentCards.getCards();

    const currentPageCards = [];
    while (idx < this.#currentPage * this.getCardsPerPage() && idx < cardBucket.length) {
      currentPageCards.push(cardBucket[idx]);
      idx++;
    }

    return currentPageCards;
  }

  /**
   * Returns whether the current page is the last page of queried cards.
   * @returns {boolean} True if current page is last page of cards that satisfies applied query, false otherwise.
   */
  isLastPage() {
    return this.#lastPage;
  }

  getExpandedView() {
    return this.#expandedView;
  }

  /**
   * How many cards a page holds, which depends on the mode and so has to be asked for rather than assumed.
   *
   * The single source for every consumer — this container's page math, and ExpandedView's paging and position
   * indicator. Two copies of this number disagreeing is a review queue that skips or repeats a label at each page
   * boundary, which looks like nothing at all until someone audits the results.
   *
   * @returns {number} Cards per page in the mode this container is in.
   */
  getCardsPerPage() {
    return this.#listMode ? CardContainer.#listCardsPerPage : CardContainer.#filteredCardsPerPage;
  }

  /** @returns {boolean} Whether the page is showing an explicit `?labelIds=` review list (#5444). */
  isListMode() {
    return this.#listMode;
  }

  /** @returns {number} How many labels the review list actually holds; 0 when not in list mode. */
  getListSize() {
    return this.#listMode ? this.#currentCards.getSize() : 0;
  }

  /**
   * Opens a review list's card for a label by its position in the list, paging to it first if it's on another page.
   *
   * This is what a `?labelId=` deep link uses in list mode: opening the label by id instead would leave the expanded
   * view with no reference card, so prev/next would restart from the top of the list and the position indicator
   * would have nothing to count from.
   *
   * @param {number} labelId - The label to open.
   * @returns {boolean} True if the list holds that label and it is being opened.
   */
  jumpToLabel(labelId) {
    if (!this.#listMode) return false;
    const index = this.#currentCards.getCards().findIndex((card) => card.getLabelId() === labelId);
    if (index < 0) return false;

    this.#expandedView.pendingCardIndex = index;
    const page = Math.floor(index / this.getCardsPerPage()) + 1;
    if (page === this.#currentPage) {
      // This page's cards are already rendered (render() is what calls restoreFromUrl), so open it right away.
      this.#expandedView.onPageCardsRendered();
    } else {
      this.#setPage(page);
      this.updateCardsNewPage();
    }
    return true;
  }

  /**
   * Writes the review list's size into the strip, replacing the count the page was rendered with.
   *
   * The server-rendered number is how many ids were *asked for*; this is how many came back. A list that came back
   * whole just says how many labels there are; a short one says both numbers, so the gap is on screen rather than
   * only inside the unavailable disclosure.
   *
   * @param {number} shown - How many labels the list is showing.
   */
  static #renderListCount(shown) {
    const countEl = document.getElementById('gallery-list-count');
    if (!countEl) return;
    const requested = Number(countEl.dataset.requested);
    countEl.textContent = shown < requested
      ? i18next.t('gallery:list-count-partial', { shown, count: requested })
      : i18next.t('gallery:list-count', { count: shown });
  }

  /**
   * Restates the truncation notice the page was rendered with, in the language the rest of the panel is now in.
   *
   * Both numbers come off the element the server wrote them onto — how many ids were dropped, and the cap that
   * dropped them — so the limit is never a frontend literal. A list that fits the cap renders no such element.
   * (The strip's other lines are static text and localize themselves through `data-i18n`.)
   */
  static #renderListTruncated() {
    const truncatedEl = document.getElementById('gallery-list-truncated');
    if (!truncatedEl) return;
    const dropped = Number(truncatedEl.dataset.dropped);
    const max = Number(truncatedEl.dataset.max);
    if (dropped > 0) truncatedEl.textContent = i18next.t('gallery:list-truncated', { count: dropped, max });
  }

  /** Tells the reviewer the list could not be loaded, rather than letting a failed request read as an empty queue. */
  static #showListError() {
    const errorEl = document.getElementById('gallery-list-error');
    if (errorEl) errorEl.hidden = false;
  }

  /**
   * Fills the strip's unavailable disclosure, so a short list reads as explained rather than broken.
   *
   * Behind a disclosure rather than inline: the usual case is that every id came back, and a reviewer who needs to
   * chase the ones that didn't is the only one who wants a few hundred numbers on screen.
   *
   * @param {number[]|undefined} labelIds - The unavailable ids, in the order they were requested.
   */
  static #renderUnavailableIds(labelIds) {
    const disclosure = document.getElementById('gallery-list-unavailable');
    if (!disclosure) return;
    const list = disclosure.querySelector('.gallery-list-bar__ids');
    const summary = disclosure.querySelector('summary');
    if (!list || !summary || !labelIds || labelIds.length === 0) {
      disclosure.hidden = true;
      return;
    }
    summary.textContent = i18next.t('gallery:list-unavailable', { count: labelIds.length });
    list.replaceChildren(...labelIds.map((labelId) => {
      const item = document.createElement('li');
      item.textContent = `#${labelId}`;
      return item;
    }));
    disclosure.hidden = false;
  }
}
